import {
  getBlockFixture,
  getPageFixture,
  MATRIX_LOCALES,
  type MatrixLocale,
} from '@web-studio/core-fixtures';
import {
  COMPONENT_TYPE_REGISTRY_KEYS,
  getColorSetsForTheme,
  getThemeRegistryEntry,
  isGalleryLightbox,
  isNavPresentation,
  LAYOUT_COMPONENT_TYPE,
  navPresentationPrefersLogo,
  resolveNavLogoAssetId,
  SPACING_ENUM_KEY_VALUES,
  type LayoutBlock,
  type LayoutComponentType,
  type NavPresentation,
  type RegistryBlockPresentation,
  type RegistryBlockType,
  type SpacingEnumKey,
} from '@web-studio/shared-schemas';
import {
  blockTypeForComponentType,
  defaultLookForBlockType,
  listLabArrangements,
  listLabBlockOptions,
  parseLabLook,
  resolveLabArrangement,
} from '@/lib/blockAxes';

/** Same path contract as studio-shell `resolvePlatformDefaultNavLogoPath`. */
function labPlatformNavLogoPath(
  themeId: string,
  colorsetId: string,
  look: NavPresentation,
): string {
  const vertical = getThemeRegistryEntry(themeId)?.allowed_verticals[0];
  const { mark, surface } = resolveNavLogoAssetId(vertical, themeId, look, colorsetId);
  return `/platform/defaults/logos/nav-mark-${mark}-${surface}.svg`;
}

export interface MatrixViewState {
  componentType: LayoutComponentType;
  blockType: RegistryBlockType;
  arrangementId: string;
  presentation: RegistryBlockPresentation;
  look: string | null;
  themeId: string;
  colorsetId: string;
  spacingEnum: SpacingEnumKey;
  locale: MatrixLocale;
  fixtureId: string;
  viewport: 'desktop' | 'tablet' | 'mobile';
}

export interface PageViewState {
  pageFixtureId: string;
  themeId: string;
  colorsetId: string;
  spacingEnum: SpacingEnumKey;
  locale: MatrixLocale;
  viewport: 'desktop' | 'tablet' | 'mobile';
}

function resolveAxesFromComponent(
  componentType: LayoutComponentType,
  fixtureParam: string | null,
  arrangementParam: string | null,
  lookParam: string | null,
): Pick<
  MatrixViewState,
  'componentType' | 'blockType' | 'arrangementId' | 'presentation' | 'look' | 'fixtureId'
> {
  let blockType =
    blockTypeForComponentType(componentType) ?? listLabBlockOptions()[0]!.type;
  let preferredFixture =
    fixtureParam ??
    listLabArrangements(blockType).find((entry) => entry.componentType === componentType)
      ?.fixtureId ??
    null;
  let arrangement = resolveLabArrangement(
    blockType,
    arrangementParam ?? undefined,
    preferredFixture ?? undefined,
  );
  // Implemented types without Design Lab fixtures (e.g. legal) → first reviewable block.
  if (!arrangement) {
    const fallbackBlock = listLabBlockOptions()[0];
    if (fallbackBlock) {
      blockType = fallbackBlock.type;
      arrangement = listLabArrangements(blockType)[0];
    }
  }
  const resolved = arrangement;
  if (!resolved) {
    return {
      componentType,
      blockType,
      arrangementId: '',
      presentation: (COMPONENT_TYPE_REGISTRY_KEYS[componentType] ??
        componentType) as RegistryBlockPresentation,
      look: defaultLookForBlockType(blockType) ?? null,
      fixtureId: '',
    };
  }

  const look = parseLabLook(blockType, lookParam, resolved.presentation);

  return {
    componentType: resolved.componentType,
    blockType,
    arrangementId: resolved.id,
    presentation: resolved.presentation,
    look,
    fixtureId: resolved.fixtureId,
  };
}

export function parseMatrixSearchParams(
  params: URLSearchParams,
  defaultComponent: LayoutComponentType,
): MatrixViewState {
  const themeId = params.get('theme') ?? 'michelin-dark';
  const themeEntry = getThemeRegistryEntry(themeId);
  const colorsets = getColorSetsForTheme(themeId);
  const colorsetId =
    params.get('colorset') ?? colorsets[0]?.id ?? themeEntry?.default_colorset_id ?? '';

  const spacingParam = params.get('spacing');
  const spacingEnum = (
    SPACING_ENUM_KEY_VALUES.includes(spacingParam as SpacingEnumKey)
      ? spacingParam
      : 'balanced'
  ) as SpacingEnumKey;

  const localeParam = params.get('locale');
  const locale = (
    MATRIX_LOCALES.includes(localeParam as MatrixLocale) ? localeParam : 'en'
  ) as MatrixLocale;

  const componentType = (params.get('component') ??
    defaultComponent) as LayoutComponentType;

  const axes = resolveAxesFromComponent(
    componentType,
    params.get('fixture'),
    params.get('arrangement'),
    params.get('look'),
  );

  const viewportParam = params.get('viewport');
  const viewport =
    viewportParam === 'mobile' || viewportParam === 'tablet' ? viewportParam : 'desktop';

  return {
    ...axes,
    themeId,
    colorsetId,
    spacingEnum,
    locale,
    viewport,
  };
}

export function matrixStateToSearchParams(state: MatrixViewState): URLSearchParams {
  const p = new URLSearchParams();
  p.set('theme', state.themeId);
  p.set('colorset', state.colorsetId);
  p.set('spacing', state.spacingEnum);
  p.set('locale', state.locale);
  p.set('fixture', state.fixtureId);
  p.set('arrangement', state.arrangementId);
  if (state.look) {
    p.set('look', state.look);
  }
  if (state.viewport !== 'desktop') {
    p.set('viewport', state.viewport);
  }
  return p;
}

export function parsePageSearchParams(params: URLSearchParams): PageViewState {
  const pageFixtureId = params.get('page') ?? 'restaurant-minimum';
  const page = getPageFixture(pageFixtureId);
  const themeId = params.get('theme') ?? page?.defaultThemeId ?? 'michelin-dark';
  const colorsets = getColorSetsForTheme(themeId);
  const colorsetId = params.get('colorset') ?? colorsets[0]?.id ?? '';

  const spacingParam = params.get('spacing');
  const spacingEnum = (
    SPACING_ENUM_KEY_VALUES.includes(spacingParam as SpacingEnumKey)
      ? spacingParam
      : 'balanced'
  ) as SpacingEnumKey;

  const localeParam = params.get('locale');
  const locale = (
    MATRIX_LOCALES.includes(localeParam as MatrixLocale) ? localeParam : 'en'
  ) as MatrixLocale;

  const viewportParam = params.get('viewport');
  const viewport =
    viewportParam === 'mobile' || viewportParam === 'tablet' ? viewportParam : 'desktop';

  return {
    pageFixtureId: page?.id ?? pageFixtureId,
    themeId,
    colorsetId,
    spacingEnum,
    locale,
    viewport,
  };
}

export function resolveFixtureBlocks(fixtureId: string) {
  const fixture = getBlockFixture(fixtureId);
  return fixture ? [fixture.block] : [];
}

export interface LabChromeParams {
  engineer: boolean;
  editTargets: boolean;
  parity: boolean;
}

export function parseLabChromeParams(params: URLSearchParams): LabChromeParams {
  return {
    engineer: params.get('mode') === 'engineer',
    editTargets: params.get('targets') === '1',
    parity: params.get('parity') === '1',
  };
}

/** Apply Arrangement + Look axes onto a fixture block for canvas preview. */
export function applyMatrixAxesToBlock(
  block: LayoutBlock,
  state: Pick<
    MatrixViewState,
    'presentation' | 'componentType' | 'look' | 'themeId' | 'colorsetId'
  >,
): LayoutBlock {
  const next: LayoutBlock = {
    ...block,
    componentType: state.componentType,
    registryBlockKey: state.presentation,
  };
  if (!state.look) return next;

  if (
    state.componentType === LAYOUT_COMPONENT_TYPE.imageGallery &&
    isGalleryLightbox(state.look)
  ) {
    return {
      ...next,
      content: {
        ...next.content,
        galleryLightbox: {
          de: state.look,
          en: state.look,
          vi: state.look,
        },
      },
    };
  }

  if (!isNavPresentation(state.look)) return next;

  const withLook: LayoutBlock = {
    ...next,
    content: {
      ...next.content,
      navPresentation: {
        de: state.look,
        en: state.look,
        vi: state.look,
      },
    },
  };

  // Match studio bootstrap: classic / editorial / conversion = typography-only brand;
  // overlay / capsule / … seed the platform mark (never Unsplash stock photos).
  const media: Record<string, string> = { ...(withLook.media ?? {}) };
  if (navPresentationPrefersLogo(state.look)) {
    media.logoImage = labPlatformNavLogoPath(state.themeId, state.colorsetId, state.look);
  } else {
    delete media.logoImage;
  }
  return {
    ...withLook,
    media,
  };
}
